import { Link } from "react-router-dom";
import styled from "styled-components";
import { designTokens } from "../../theme/designTokens";

/* Properties Page Layout Container */
const PropertiesPage = styled.div`
  display: flex;
  justify-content: center;
  gap: ${designTokens.spacing.xl};
  padding: 0 0 ${designTokens.spacing.xl} 0;

  @media (max-width: 900px) {
    flex-direction: column-reverse;
    gap: 0;
    margin: 0;
  }
`;

/* Properties Page Header */
const PropertiesPageHeader = styled.h2`
  margin-bottom: ${designTokens.spacing.lg};
  font-size: 36px;
  font-weight: 700;
  color: ${designTokens.colors.neutral.darker};
  text-align: center;
  letter-spacing: 0.5px;

  @media (max-width: ${designTokens.breakpoints.tablet}) {
    font-size: 28px;
  }

  @media (max-width: ${designTokens.breakpoints.mobile}) {
    font-size: 20px;
  }
`;

/* Pagination Container */
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 40px 0 0 0 !important;
  gap: ${designTokens.spacing.sm};
`;

/* Properties List Container */
const PropertiesPageList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
    margin: 0;
  }
`;

/* Property Card - Main Container with Modern Design */
const PropertyPageCard = styled(Link)`
  overflow: hidden;
  text-decoration: none;
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) backwards;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(194, 153, 87, 0.25);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

/* Property Image Container with Hover Effects */
const PropertyImageContainer = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
  background: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${PropertyPageCard}:hover & img {
    transform: scale(1.08);
  }

  /* Image Overlay on Hover */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.1) 50%,
      rgba(0, 0, 0, 0.3) 100%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }

  ${PropertyPageCard}:hover &::after {
    opacity: 1;
  }
`;

/* Property Category Badge - Modern Style */
const PropertyCategory = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  color: #1a1a1a;
  border-radius: 30px;
  padding: 8px 16px;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.5px;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  transition: all 0.3s ease;

  /* Special styling for sale/rent */
  &[data-type="sale"] {
    background: #c29957;
    color: white;
  }

  &[data-type="rent"] {
    background: #3b82f6;
    color: white;
  }

  ${PropertyPageCard}:hover & {
    transform: translateY(-3px);
  }
`;

/* Property Posted Date Badge - Hidden on Hover */
const PropertyPostedDate = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  border-radius: 25px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  z-index: 2;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 6px;

  ${PropertyPageCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* Property Info Section - Elegant Spacing */
const PropertyPageInfo = styled.div`
  padding: 16px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* Property Type/Category Title */
const PropertyPageType = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f8f6f3;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  text-transform: capitalize;
  margin-top: 12px;
  width: fit-content;
`;

/* Property Price - Elegant Typography */
const PropertyPagePrice = styled.div`
  font-family: "Playfair Display", Georgia, serif;
  font-size: 26px;
  font-weight: 700;
  color: #c29957;
  margin-bottom: 4px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

/* Price Details (e.g., per month) */
const PropertyPriceDetail = styled.span`
  font-family:
    "Outfit",
    -apple-system,
    sans-serif;
  font-size: 13px;
  color: #6b6b6b;
  font-weight: 400;
  margin-left: 4px;
`;

/* Property Name/Title - Refined Typography */
const PropertyPageName = styled.h3`
  font-family: "Playfair Display", Georgia, serif;
  font-size: 19px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
  text-transform: capitalize;
  transition: color 0.3s ease;

  ${PropertyPageCard}:hover & {
    color: #c29957;
  }
`;

/* Property Address - Subtle with Icon Space */
const PropertyPageAddress = styled.div`
  font-size: 14px;
  color: #6b6b6b;
  margin-bottom: 10px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 6px;

  /* For location icon if you add one */
  svg {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
`;

/* Property Features Container - Clean Grid */
const PropertyPageDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 10px 0 0 0;
  border-top: 1px solid #e8e8e8;
  margin-top: auto;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
`;

/* Individual Property Feature - Icon + Value Layout */
const PropertyPageDetail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  transition: transform 0.3s ease;

  ${PropertyPageCard}:hover & {
    transform: translateY(-2px);
  }

  svg {
    width: 20px;
    height: 20px;
    color: #c29957;
    transition: all 0.3s ease;
  }

  ${PropertyPageCard}:hover & svg {
    transform: scale(1.1);
    color: #a67c42;
  }

  /* Feature value (number) */
  span:first-of-type {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
  }

  /* Feature label */
  span:last-of-type {
    font-size: 10px;
    color: #6b6b6b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
`;

/* Gallery Icon Button - Kept from original but refined */
const PropertyGalleryIcon = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  color: #c29957;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  z-index: 2;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #c29957;
    color: white;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
`;

export {
  PropertiesPage,
  PropertiesPageHeader,
  Pagination,
  PropertiesPageList,
  PropertyPageCard,
  PropertyImageContainer,
  PropertyCategory,
  PropertyPostedDate,
  PropertyPageInfo,
  PropertyPageType,
  PropertyPagePrice,
  PropertyPriceDetail,
  PropertyPageName,
  PropertyPageAddress,
  PropertyPageDetails,
  PropertyPageDetail,
  PropertyGalleryIcon,
};
